import { useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import { ApolloCache, FetchResult } from '@apollo/client';
import {
  TimeSignalsDocument,
  useUpdateTimeSignalMutation,
  TimeSignalClassification,
  TimeSignalsQuery,
  UpdateTimeSignalMutation,
  Label,
  SortOrder,
  usePutLabelSuggestionsMutation,
  LabelType,
  PutLabelSuggestionsMutation
} from 'graphql/types';
import { mapDateToISOString } from 'utils/date-time';
import {
  useTimeSignalGetParam,
  TimeSignalGetParam
} from 'modules/notes/hooks/useTimeSignalParam';
import { NotesToastContext } from 'modules/notes/NotesToastContextProvider';
import {
  NotesClassification,
  NOTES_PAGE_TYPE,
  useNotesFilterContext
} from 'modules/notes/NotesFilterContextProvider';
import { useNotesSortContext } from 'modules/notes/NotesSortContextProvider';
import { SearchCriteria } from 'modules/notes/common';
import { useMeContext } from 'modules/me';
import { TIMELINE_TIMESIGNAL_COUNT } from 'modules/notes/Timeline';
import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks/useIsSmartSuggestionSettingEnabled';
import {
  CustomLabel,
  TimesheetLabel
} from 'modules/timesheet-labels/labelSchema';
import {
  appendTimeSignals,
  canAddNoteToCache,
  isSortRequired,
  shouldAddToCache,
  updateCachedTimeSignalsWithFederatedSuggestions
} from 'modules/notes/cache/utils/cache-utils';
import { NoteData } from 'modules/notes/common/noteSchema';
import { clearCachedDates } from 'modules/notes/cache/utils/time-signal-utils';
import { useFeatureFlag } from 'modules/common/hooks';
import { mapTimesheetLabel, truncateMessage } from '../../common/utils';
import { getTime } from './useCreateTimeSignal';

export const updateCache = ({
  param,
  selectedClassification,
  searchCriteria,
  sortOrder
}: {
  param: TimeSignalGetParam;
  selectedClassification: NotesClassification;
  searchCriteria: SearchCriteria;
  sortOrder: SortOrder;
}) => (
  cache: ApolloCache<UpdateTimeSignalMutation>,
  mutationResult: FetchResult<
    UpdateTimeSignalMutation,
    Record<string, any>,
    Record<string, any>
  >
): void => {
  if (mutationResult?.data?.updateTimeSignal?.timeSignal) {
    const timeSignal = mutationResult.data.updateTimeSignal.timeSignal;
    const existingSignals: TimeSignalsQuery | null = cache.readQuery({
      query: TimeSignalsDocument,
      variables: param
    });
    if (existingSignals) {
      const canUpdateCache = canAddNoteToCache(
        param,
        selectedClassification,
        timeSignal,
        searchCriteria
      );

      const shouldAdd = shouldAddToCache(
        existingSignals,
        timeSignal,
        sortOrder
      );

      const updatedSignals =
        canUpdateCache && shouldAdd
          ? existingSignals!.timeSignals.items.map((t) => {
              if (t.id === timeSignal.id) return timeSignal;
              return t;
            })
          : existingSignals!.timeSignals.items.filter(
              (t) => t.id !== timeSignal.id
            );

      const sortRequired =
        canUpdateCache && shouldAdd
          ? isSortRequired(existingSignals, timeSignal, sortOrder)
          : false;

      const orderedSignals = sortRequired
        ? appendTimeSignals(existingSignals, timeSignal, sortOrder)
        : updatedSignals;

      const totalCount = canUpdateCache
        ? existingSignals.timeSignals.totalCount
        : existingSignals.timeSignals.totalCount - 1;

      cache.writeQuery({
        query: TimeSignalsDocument,
        variables: param,
        data: {
          timeSignals: {
            __typename: 'TimeSignalsResult',
            items: [...orderedSignals],
            cursor: existingSignals.timeSignals.cursor,
            totalCount
          }
        }
      });
    }
  }
};

const updateCacheWithFederatedSuggestions = (param: TimeSignalGetParam) => (
  cache: ApolloCache<UpdateTimeSignalMutation>,
  mutationResult: FetchResult<
    PutLabelSuggestionsMutation,
    Record<string, any>,
    Record<string, any>
  >
): void => {
  if (mutationResult?.data?.putLabelSuggestions) {
    updateCachedTimeSignalsWithFederatedSuggestions(
      cache,
      mutationResult?.data?.putLabelSuggestions,
      param
    );
  }
};

const acceptSuggestion = (labels: TimesheetLabel[]): TimesheetLabel[] => {
  return labels.map((label) => {
    if (
      !label.value?.idValue &&
      !label.value?.stringValue &&
      !label.value?.numericValue
    ) {
      return {
        ...label,
        value: label.suggestedValues?.[0]
      };
    }

    return label;
  });
};

export const useUpdateTimeSignal = ({
  onClose,
  showToastWithContext,
  initialLabelValue
}: {
  onClose?: () => void;
  showToastWithContext: (input: NotesToastContext) => void;
  initialLabelValue?: CustomLabel[];
}): { onEditNote: (note: NoteData) => Promise<void>; loading: boolean } => {
  const {
    filterContext: { selectedClassification, searchCriteria, pageType }
  } = useNotesFilterContext();
  const { sortOrder } = useNotesSortContext();
  const param = useTimeSignalGetParam(
    pageType === NOTES_PAGE_TYPE.TIMELINE
      ? TIMELINE_TIMESIGNAL_COUNT
      : undefined
  );
  const { id, locale } = useMeContext() || {};
  const ianaName = locale?.timeZone?.ianaName;
  const customLabelValuesMap = useMemo(
    () =>
      (initialLabelValue ?? []).reduce((acc, label) => {
        acc[label.id] = { ...label, value: null };
        return acc;
      }, {} as Record<string, CustomLabel>),
    [initialLabelValue]
  );
  const [
    editNote,
    { loading: editNoteLoading, error: editNoteError }
  ] = useUpdateTimeSignalMutation();
  const [
    putSuggestions,
    { loading: putSuggestionsLoading, error: putSuggestionsError }
  ] = usePutLabelSuggestionsMutation();

  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );
  const me = useMeContext();
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  const onEditNote = useCallback(
    async (note: NoteData) => {
      if (
        (!editNoteLoading && editNoteError) ||
        (!putSuggestionsLoading && putSuggestionsError)
      ) {
        showToastWithContext({
          showError: true,
          message: 'toast.note.failure'
        });
      }

      let startTime = note.startTime;
      let endTime = note.endTime;
      if (note.date && startTime) {
        startTime = getTime(note.date, startTime, ianaName);
      }
      if (note.date && endTime) {
        endTime = getTime(note.date, endTime, ianaName);
      }

      const consolidatedLabelMap = (note.customLabels ?? []).reduce(
        (acc, l) => {
          acc[l.id] = l;
          return acc;
        },
        customLabelValuesMap as Record<string, CustomLabel>
      );
      let labels: Label[] = [
        ...(note.timesheetLabels ?? []),
        ...Object.values(consolidatedLabelMap)
      ];
      if (
        isTimeSignalFederatedSuggestionsEnabled &&
        note.classification === TimeSignalClassification.Accepted
      ) {
        labels = acceptSuggestion(labels);
      }
      clearCachedDates(id);

      if (
        isTimeSignalFederatedSuggestionsEnabled &&
        note.id &&
        me?.id &&
        note.labelValuesInferenceId
      ) {
        await putSuggestions({
          variables: {
            input: {
              userUri: me.id,
              inferenceId: note.labelValuesInferenceId,
              timeSignalId: note.id,
              startTime: mapDateToISOString(startTime),
              endTime: mapDateToISOString(endTime),
              duration: note.duration ? note.duration : null,
              suggestions: labels
                .filter(
                  (label) => Boolean(label) && label.type !== LabelType.Comment
                )
                .map(
                  (l: TimesheetLabel) =>
                    ({
                      labelId: l.id,
                      suggestionMetadata: l.suggestionMetadata,
                      thresholdMet: l.thresholdMet ?? false,
                      suggestedValues: l.suggestedValues ?? []
                    } as any)
                )
            }
          },
          update: updateCacheWithFederatedSuggestions(param)
        });
      }

      await editNote({
        variables: {
          input: {
            id: note.id as string,
            classification:
              note.classification ?? TimeSignalClassification.Unclassified,
            description: note.note,
            startTime: mapDateToISOString(startTime),
            endTime: mapDateToISOString(endTime),
            duration: note.duration ? note.duration : null,
            labelValues: labels.map((l) =>
              mapTimesheetLabel(
                isSmartSuggestionEnabled,
                l,
                isTimeSignalFederatedSuggestionsEnabled
              )
            ),
            labelValuesInferenceId: note.labelValuesInferenceId,
            date: note.date ? DateTime.fromJSDate(note.date).toISODate() : null
          }
        },
        update: updateCache({
          param,
          selectedClassification,
          searchCriteria,
          sortOrder
        }),
        awaitRefetchQueries: false,
        refetchQueries: ['timeSignalsCount', 'timeSignalsTotals']
      });

      if (
        !editNoteLoading &&
        !editNoteError &&
        (isTimeSignalFederatedSuggestionsEnabled
          ? !putSuggestionsLoading && !putSuggestionsError
          : true)
      ) {
        showToastWithContext({
          showSuccess: true,
          message: 'toast.note.noteUpdated',
          values: { message: truncateMessage(note.note ?? '') }
        });
      }

      if (onClose) {
        onClose();
      }
    },
    [
      editNoteLoading,
      editNoteError,
      putSuggestionsLoading,
      putSuggestionsError,
      customLabelValuesMap,
      id,
      editNote,
      param,
      selectedClassification,
      searchCriteria,
      sortOrder,
      isTimeSignalFederatedSuggestionsEnabled,
      me?.id,
      onClose,
      showToastWithContext,
      ianaName,
      isSmartSuggestionEnabled,
      putSuggestions
    ]
  );

  return { onEditNote, loading: editNoteLoading || putSuggestionsLoading };
};
